import '../styles/globals.css'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { PostHogProvider } from 'posthog-js/react'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
})

const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-11-30',
} as const

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => (
    <div className="mt-6 flex-col pb-6 sm:w-full">
      <div className="rounded-lg border-2 border-serblue-light text-lg p-6 md:w-1/2">
        <h4 className="mb-2 text-xl font-bold text-gray-800">404 Error</h4>
        We were unable to find the page you are looking for.
      </div>
    </div>
  ),
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const PosthogProviderProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  if (!import.meta.env.VITE_PUBLIC_POSTHOG_KEY) return <>{children}</>
  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={posthogOptions}
    >
      {children}
    </PostHogProvider>
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('No root element found')
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement)
  root.render(
    <StrictMode>
      <PosthogProviderProvider>
        <RouterProvider router={router} />
      </PosthogProviderProvider>
    </StrictMode>,
  )
}
